import { LegalOnboardingStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { toaster } from "@/components/ui/toaster";
import {
  getLegalOnboardingRequestDetail,
  UpdateLegalOnboardingDocumentoBody,
  confirmApproveLegalOnboarding,
  createLegalOnboardingAccountInBind,
  PersonType,
  updateLegalOnboardingRequest,
} from "@/services/legal-onboarding.service";
import {
  LegalOnboardingDocumentStatusIds,
  LegalOnboardingStatusIds,
} from "@/constants/enums";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import type { DocDecision } from "../components/doc-list/types";
export type { DocDecision } from "../components/doc-list/types";
import { SOCIETIES } from "@/constants/legal/societies/societies";

export type LegalOnboardingDocumentTipo = "Obligatorio" | "Opcional";

export interface LegalOnboardingDocument {
  id: number;
  idDocumento?: number;
  nombre: string;
  urlDescarga?: string;
  obligatorio?: boolean;
  idTipoArchivo?: number;
  tipo?: LegalOnboardingDocumentTipo;
  estado: string | null;
  requiereReenvio: boolean;
  [key: string]: unknown;
}

export interface LegalOnboardingPersonData {
  nombre: string;
  apellido: string;
  mail: string;
  celular: string | null;
  [key: string]: unknown;
}

export type LegalOnboardingDomicilio = {
  calle: string;
  numero: string;
  piso: string;
  localidadId: number;
  provinciaId: number;
  codigoPostal: string;
};

export interface LegalOnboardingDetail {
  id: number;
  cuit: string;
  cuitCuil?: string;
  tipoSociedad?: string;
  razonSocial?: string;
  estado: LegalOnboardingStatus;
  documentos: LegalOnboardingDocument[];
  datosPersona: LegalOnboardingPersonData | null;
  [key: string]: unknown;
}

type DocCatalogItem = {
  idTipoArchivo: number;
  nombre: string;
  tipo: "Obligatorio" | "Opcional";
};

const docsByTipoSociedad: Record<string, DocCatalogItem[]> = Object.fromEntries(
  SOCIETIES.map((s) => [
    s.name,
    s.documents.map((d) => ({
      idTipoArchivo: d.id,
      nombre: d.name,
      tipo: d.required ? "Obligatorio" : "Opcional",
    })),
  ]),
);

const normalizeSocietyKey = (value: string) =>
  value
    .normalize("NFD")
    .replaceAll(/[\u0300-\u036f]/g, "")            
    .toLowerCase()
    .replaceAll(/\bsociedad\b/g, "soc")            
    .replaceAll(/\bresponsabilidad\b/g, "resp")
    .replaceAll(/\blimitada\b/g, "ltda")
    .replaceAll(/\bacciones\b/g, "acc")
    .replaceAll(/\bsimplificadas?\b/g, "sas")
    .replaceAll(/\banonima\b/g, "sa")
    .replaceAll(/\bde\b/g, "")                      
    .replaceAll(/[^a-z0-9]+/g, "")                 
    .trim();

const docsByTipoSociedadNormalized: Record<string, DocCatalogItem[]> =
  Object.fromEntries(
    Object.entries(docsByTipoSociedad).map(([k, v]) => [
      normalizeSocietyKey(k),
      v,
    ]),
  );

const normalizeDocName = normalizeSocietyKey;

const getDocTipo = (d: any) => {
  let tipo = d?.tipo;
  if (typeof d?.obligatorio === "boolean") {
    tipo = d.obligatorio ? "Obligatorio" : "Opcional";
  }

  return tipo;
};

const getFiniteNumber = (value: any) => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

const mapDocsWithoutCatalog = (documentos?: any[]) =>
  (documentos ?? []).map((d) => {
    const tipo = getDocTipo(d);

    const idTipoArchivo = getFiniteNumber(d?.idTipoArchivo);
    const hasTipoId = idTipoArchivo !== null;

    return {
      ...d,
      idDocumento: d?.id,
      id: hasTipoId ? idTipoArchivo : d?.id,
      idTipoArchivo: hasTipoId ? idTipoArchivo : d?.idTipoArchivo,
      tipo,
    };
  });

const buildLoadedIndexes = (documentos?: any[]) => {
  const loaded = (documentos ?? []).filter(Boolean);
  const loadedMap = new Map<number, any>();
  const loadedByName = new Map<string, any>();

  for (const d of loaded) {
    const idTipoArchivo = getFiniteNumber(d?.idTipoArchivo ?? d?.id);
    if (idTipoArchivo === null) continue;
    if (!loadedMap.has(idTipoArchivo)) loadedMap.set(idTipoArchivo, d);

    const docName = typeof d?.nombre === "string" ? d.nombre : "";
    if (!docName.trim().length) continue;
    const normalized = normalizeDocName(docName);
    if (normalized.length && !loadedByName.has(normalized)) {
      loadedByName.set(normalized, d);
    }
  }

  return { loadedMap, loadedByName };
};

const buildMissingCatalogDoc = (e: DocCatalogItem) => ({
  id: e.idTipoArchivo,
  idTipoArchivo: e.idTipoArchivo,
  nombre: e.nombre,
  tipo: e.tipo,
  obligatorio: e.tipo === "Obligatorio",
  estado: null,
  requiereReenvio: false,
  urlDescarga: undefined,
});

const mergeCatalogDoc = (e: DocCatalogItem, fromBe: any) => ({
  ...fromBe,
  idDocumento: fromBe?.id,
  id: e.idTipoArchivo,
  idTipoArchivo: e.idTipoArchivo,
  nombre: e.nombre,
  tipo: e.tipo,
  obligatorio:
    typeof fromBe?.obligatorio === "boolean" ? fromBe.obligatorio : e.tipo === "Obligatorio",
  estado: fromBe?.estado ?? null,
  requiereReenvio: Boolean(fromBe?.requiereReenvio),
});

const mergeDocsForSociety = (tipoSociedad?: string, documentos?: any[]) => {
  const expected = tipoSociedad
    ? docsByTipoSociedadNormalized[normalizeSocietyKey(tipoSociedad)]
    : undefined;
  if (!expected?.length) {
    return mapDocsWithoutCatalog(documentos);
  }

  const { loadedMap, loadedByName } = buildLoadedIndexes(documentos);

  return expected.map((e) => {
    const fromBe =
      loadedMap.get(e.idTipoArchivo) ?? loadedByName.get(normalizeDocName(e.nombre));
    if (!fromBe) {
      return buildMissingCatalogDoc(e);
    }

    return mergeCatalogDoc(e, fromBe);
  });
};



interface FetchDetailHandlerArgs {
  id: number;
  setLoadingDetail?: (v: boolean) => void;
  withLoading?: boolean;
  setDetail: (v: LegalOnboardingDetail) => void;
  setDecisions: (v: Record<number, DocDecision>) => void;
  setReviewStarted: (v: boolean) => void;
  cancelled?: () => boolean;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

const getInitialDocDecision = (d: any): DocDecision => {
  if (d?.requiereReenvio) return "pending_resend";

  const estadoRaw = d?.estado;
  const estadoIdRaw =
    d?.idEstadoDocumento ??
    d?.idEstado ??
    d?.estadoId ??
    d?.estado?.id ??
    d?.estado?.Id;

  let estadoString: string | null = null;
  if (typeof estadoRaw === "string") {
    estadoString = estadoRaw;
  } else if (typeof d?.estado?.nombre === "string") {
    estadoString = d.estado.nombre;
  }

  if (estadoString === "Aceptado") return "accept";
  if (estadoString === "Rechazado") return "reject";

  const estadoId = typeof estadoIdRaw === "number" ? estadoIdRaw : Number(estadoIdRaw);
  if (Number.isFinite(estadoId)) {
    if (estadoId === LegalOnboardingDocumentStatusIds.Aceptado) return "accept";
    if (estadoId === LegalOnboardingDocumentStatusIds.Rechazado) return "reject";
  }

  return null;
};

export const fetchAndMapLegalOnboardingDetailHandler = async ({
  id,
  setLoadingDetail,
  withLoading = true,
  setDetail,
  setDecisions,
  setReviewStarted,
  cancelled,
  navigate,
  fetchPermissions,
  routeState,
}: FetchDetailHandlerArgs) => {
  try {
    if (withLoading) setLoadingDetail?.(true);
    const response = await getLegalOnboardingRequestDetail({ id });

    if (cancelled?.()) return;

    const mergedDocs = mergeDocsForSociety(
      response?.tipoSociedad,
      response?.documentos,
    ) as LegalOnboardingDocument[];
    const mergedDetail: LegalOnboardingDetail = {
      ...(response as any),
      cuitCuil: response?.cuit,
      documentos: mergedDocs,
    };

    setDetail(mergedDetail);

    const initialDecisions = (mergedDetail?.documentos ?? []).reduce(
      (acc: Record<number, DocDecision>, d: any) => {
        const decision = getInitialDocDecision(d);
        if (decision !== null) acc[d.id] = decision;

        return acc;
      },
      {},
    );

    setDecisions(initialDecisions);
    setReviewStarted(
      mergedDetail?.estado === LegalOnboardingStatus.IN_REVIEW ||
        mergedDetail?.estado === LegalOnboardingStatus.REQUIRES_MODIF ||
        mergedDetail?.estado === LegalOnboardingStatus.APPROVED,
    );
  } catch (error) {
    if (cancelled?.()) return;

    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo cargar el detalle."),
      });
    }

    throw error;
  } finally {
    if (!cancelled?.() && withLoading) setLoadingDetail?.(false);
  }
};

interface BindAccountHandlerArgs {
  detail: LegalOnboardingDetail | null;
  domicilio: LegalOnboardingDomicilio;
}

export const bindAccountLegalOnboardingHandler = async ({
  detail,
  domicilio,
}: BindAccountHandlerArgs) => {
  if (!detail) throw new Error("Detalle no disponible");

  return await createLegalOnboardingAccountInBind({
    idSolicitud: detail.id,
    cuit: detail.cuit,
    razonSocial: detail.razonSocial ?? "",
    tipoPersona: PersonType.LegalEntity,
    domicilio,
    cuitCuil: detail.cuitCuil ?? detail.cuit,
  });
};

export type LegalOnboardingDatosCvu = {
  cuit: string;
  alias: string;
  cvu: string;
  idCuenta: number;
  idCvu: number;
};

interface ConfirmApproveHandlerArgs {
  id: number;
  documentos?: any[];
  decisions?: Record<number, DocDecision>;
  datosCvu: LegalOnboardingDatosCvu;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

const mapDecisionsToDocsPayloadForApprove = (
  documentos: any[] | undefined,
  decisions: Record<number, DocDecision> | undefined,
): UpdateLegalOnboardingDocumentoBody[] | undefined => {
  if (!documentos?.length) return undefined;

  const mapped = documentos
    .map((d) => {
      const decision = decisions?.[d?.id] ?? null;

      const rawDocId = d?.idDocumento ?? d?.id;
      const idDocumento =
        typeof rawDocId === "number" ? rawDocId : Number(rawDocId);
      if (!Number.isFinite(idDocumento)) return null;

      const estadoId =
        decision === "reject"
          ? LegalOnboardingDocumentStatusIds.Rechazado
          : LegalOnboardingDocumentStatusIds.Aceptado;

      return {
        Id: idDocumento,
        Estado: estadoId,
      } satisfies UpdateLegalOnboardingDocumentoBody;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  return mapped.length ? mapped : undefined;
};

export const confirmApproveLegalOnboardingHandler = async ({
  id,
  documentos,
  decisions,
  datosCvu,
  navigate,
  fetchPermissions,
  routeState,
}: ConfirmApproveHandlerArgs) => {
  try {
    return await confirmApproveLegalOnboarding(
      id,
      mapDecisionsToDocsPayloadForApprove(documentos, decisions),
      datosCvu,
    );
  } catch (error) {
    await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    throw error;
  }
};

interface StartReviewHandlerArgs {
  id: number;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

export const startReviewLegalOnboardingHandler = async ({
  id,
  navigate,
  fetchPermissions,
  routeState,
}: StartReviewHandlerArgs) => {
  try {
    return await updateLegalOnboardingRequest(id, {
      Id: id,
      IdEstado: LegalOnboardingStatusIds.EnRevision,
    });
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo iniciar la revisión."),
      });
    }

    throw error;
  }
};

interface RequireChangesHandlerArgs {
  id: number;
  detail: string;
  documentos?: any[];
  decisions?: Record<number, DocDecision>;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

const mapDecisionsToDocsPayload = (
  documentos: any[] | undefined,
  decisions: Record<number, DocDecision> | undefined,
): UpdateLegalOnboardingDocumentoBody[] | undefined => {
  if (!documentos?.length || !decisions) return undefined;

  const mapped = documentos
    .map((d) => {
      const decision = decisions[d?.id] ?? null;

      const rawDocId = d?.idDocumento ?? d?.id;
      const idDocumento = typeof rawDocId === "number" ? rawDocId : Number(rawDocId);
      if (!Number.isFinite(idDocumento)) return null;

      const estadoId =
        decision === "reject"
          ? LegalOnboardingDocumentStatusIds.Rechazado
          : LegalOnboardingDocumentStatusIds.Aceptado;

      return {
        Id: idDocumento,
        Estado: estadoId,
      } satisfies UpdateLegalOnboardingDocumentoBody;
    })
    .filter(
      (x): x is NonNullable<typeof x> => x !== null,
    );

  return mapped.length ? mapped : undefined;
};

export const requireChangesLegalOnboardingHandler = async ({
  id,
  detail,
  documentos,
  decisions,
  navigate,
  fetchPermissions,
  routeState,
}: RequireChangesHandlerArgs) => {
  try {
    return await updateLegalOnboardingRequest(id, {
      Id: id,
      IdEstado: LegalOnboardingStatusIds.RequiereCambios,
      Observacion: detail,
      Documentos: mapDecisionsToDocsPayload(documentos, decisions),
    });
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo requerir cambios."),
      });
    }

    throw error;
  }
};
